import * as Sentry from '@sentry/nuxt'
import pickBy from 'lodash/pickBy'
import urljoin from 'url-join'
import pkg from '../../package.json'

import { useRuntimeConfig } from '#app'
import { defineNuxtPlugin } from '#imports'
import { useAuthToken } from '~/composables/auth/useAuthToken.js'

export default defineNuxtPlugin((nuxtApp) => {
  const { apiUrl, clientId, intranet } = useRuntimeConfig().public
  const { getAuthHeaders, token } = useAuthToken()

  let _errorHandler = null
  function setErrorHandler(handler) {
    _errorHandler = handler
  }

  // 清除 error handler，避免閉包持有舊的元件引用
  function clearErrorHandler() {
    _errorHandler = null
  }

  function isValid(value) {
    return value !== undefined && value !== null
  }

  function _checkToken(req) {
    if (!req.isPublic && !req.token) {
      const error = new Error(`Token required for ${req.endpoint}`)
      error.code = 'ERR_A103'
      error.url = req.endpoint
      throw error
    }
  }

  // 請求攔截器列表
  const requestInterceptors = []

  // 註冊攔截器
  const addRequestInterceptor = (interceptor) => {
    requestInterceptors.push(interceptor)
    // 返回移除函數
    return () => {
      const index = requestInterceptors.indexOf(interceptor)
      if (index > -1) {
        requestInterceptors.splice(index, 1)
      }
    }
  }

  // 清除所有攔截器，避免閉包累積造成記憶體洩漏
  const clearAllInterceptors = () => {
    requestInterceptors.length = 0
  }

  // 取得當前攔截器數量（用於除錯）
  const getInterceptorCount = () => requestInterceptors.length

  const request = async (options) => {
    try {
      // 執行攔截器 - 允許修改 options 或進行其他處理
      for (const interceptor of requestInterceptors) {
        // 如果攔截器返回 false，則取消請求（模擬取消）
        // 注意：這裡簡單實作，實際 axios 攔截器更複雜
        const result = interceptor(options)
        if (result === false) {
          const cancelError = new Error('Canceled')
          cancelError.name = 'CanceledError'
          cancelError.__isCancel = true
          throw cancelError
        }
      }

      const { url, method, params, isPublic, headers, isFormData = false, signal } = options
      const baseUrl =
        import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : nuxtApp.$getFastApiUrl?.() || apiUrl
      const endpoint = urljoin(baseUrl, url.replace(':clientId', clientId))
      _checkToken({ isPublic, endpoint, token: token.value })
      const isGet = method.toLowerCase() === 'get'
      const payloadKey = isGet ? 'query' : 'body'
      const payload = isFormData ? params : pickBy(params, isValid)

      const res = await $fetch(endpoint, {
        method,
        headers: {
          'X-TH1GAMES-VERSION': `v${pkg.version}`,
          'X-TH1GAMES-PLATFORM': 'pwa',
          ...getAuthHeaders(),
          ...headers,
        },
        [payloadKey]: payload,
        retry: 2,
        retryDelay: 1000, // 重試延遲 1 秒（ofetch 會自動使用指數退避）
        retryStatusCodes: [408, 409, 425, 429, 500, 502, 504], // 需要重試的 HTTP 狀態碼
        signal, // 傳遞 signal 支援 AbortController
      })

      if (res.data?.error) {
        const apiError = new Error(res.data.error.message || res.data.error.code || 'API Error')
        Object.assign(apiError, res.data.error)
        throw apiError
      }
      return res
    } catch (e) {
      // 忽略取消錯誤
      if (e.__isCancel) {
        console.log('Request canceled by interceptor')
        return { data: {} }
      }

      console.error('request error:', e)
      if (e.message?.includes('Failed to fetch')) {
        Sentry.captureException(`ERR_998:${e.message}`)
        _errorHandler && _errorHandler({ code: 'ERR_998' })
      } else if (e.data) {
        _errorHandler && _errorHandler(e.data.error)
        const dataError = new Error(e.data.error?.message || e.data.error?.code || 'Request Error')
        Object.assign(dataError, e.data.error)
        throw dataError
      }
      throw e
    }
  }

  // SSR 請求結束時清理 interceptors 和 error handler
  // 避免閉包累積造成記憶體洩漏
  if (import.meta.server) {
    nuxtApp.hooks.hook('app:rendered', () => {
      clearAllInterceptors()
      clearErrorHandler()
    })
  }

  return {
    provide: {
      request: {
        send: request,
        setErrorHandler,
        addRequestInterceptor,
        clearErrorHandler,
        clearAllInterceptors,
        getInterceptorCount,
      },
    },
  }
})
